import React, { useContext, useEffect, useState } from 'react';
import { EventType, Event } from '../types/Event';
import { EventContext } from '../context/EventContext';
import { getEvent, getEventTypes, updateEvent } from '../services/eventService';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../context/ToastContext';
import { ROUTES } from '../constants/routes';
import { formatDateForBackend } from '../services/formatter.service';


const formatDateTime = (date: Date | string) => {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  // Extract local components
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  const hours = String(dateObj.getHours()).padStart(2, '0');
  const minutes = String(dateObj.getMinutes()).padStart(2, '0');

  // Format as "YYYY-MM-DDTHH:mm"
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export const EditEventDetailsPage: React.FC<{}> = ({
}) => {
  const [eventDetails, setEventDetails] = useState<Event>({} as Event);
  const [availableEventTypes, setAvailableEventTypes] = useState<EventType[]>([]);
  const navigate = useNavigate();

  const today = new Date();
  const minStartDate = (today).toISOString().slice(0, 16);

  const maxStartDate = new Date(today);
  maxStartDate.setFullYear(today.getFullYear() + 1);
  const maxStartDateStr = maxStartDate.toISOString().slice(0, 16);
  const startDate = eventDetails.start_date ? new Date(eventDetails.start_date) : today;
  const minEndDate = startDate.toISOString().slice(0, 16);
  const maxEndDate = new Date(startDate);
  maxEndDate.setDate(startDate.getDate() + 30);
  const maxEndDateStr = maxEndDate.toISOString().slice(0, 16);

  const { addToast } = useToast();

  const { currentEvent, setCurrentEvent } = useContext(EventContext);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, type, value } = e.target;
    let newValue: string | number | boolean = value;

    if (type === 'checkbox') {
      newValue = (e.target as HTMLInputElement).checked;
    } else if (type === 'radio') {
      if (value === 'true') newValue = true;
      else if (value === 'false') newValue = false;
    } else if (type === 'number') {
      newValue = value === '' ? '' : Number(value);
    }

    setEventDetails((prevDetails) => ({
      ...prevDetails,
      [name]: newValue,
    }));
  };

  const handleCancel = () => {
    navigate(ROUTES.EVENT_OVERVIEW); // Navigate back to the overview tab
  };

  const handleUpdate = async () => {
    try {
      if (eventDetails && !currentEvent?.is_locked) {
              // console.log(eventDetails.start_date, eventDetails.end_date);
              // console.log(formatDateForBackend(eventDetails.start_date), formatDateForBackend(eventDetails.end_date));
              const transformedEventDetails = {
                ...eventDetails,
                start_date: formatDateForBackend(eventDetails.start_date),
                end_date: formatDateForBackend(eventDetails.end_date), // UTC ISO string
                // transform other date fields similarly if needed
              };
        
        const event = await updateEvent(eventDetails?.event_id, transformedEventDetails);
        if (event) {
          setCurrentEvent(event);
          addToast('Event Updated.')
          navigate(ROUTES.EVENT_OVERVIEW);
        }
      }
    } catch (error) {
      addToast('Error updating event.');
      console.error(error);
    }
  };

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        if (currentEvent) {
          const event = await getEvent(currentEvent.event_id);
          if (event) {
            const transformedEvent = {
              ...event,
              use_billing_service: Boolean(event.use_billing_service),
              use_invoice: Boolean(event.use_invoice),
              has_checkin: Boolean(event.has_checkin),
              checkin_only: Boolean(event.checkin_only),
              has_invitation: Boolean(event.has_invitation)
            };
            setEventDetails(transformedEvent);
          }
        } else {
          navigate(ROUTES.EVENT_OVERVIEW);
        }
      } catch (error) {
        console.error('Error fetching event:', error);
      }
    };

    const fetchEventTypes = async () => {
      try {
        const eventTypes = await getEventTypes();
        setAvailableEventTypes(eventTypes);
      } catch (error) {
        console.error('Error fetching event tyeps');
      }
    }
    fetchEventTypes();
    fetchEvent();
  }, [currentEvent]);

  return (
    <form>
      <h2>Edit Event Details</h2>

      <div className="form-group">
        <label htmlFor="event_name">
          <strong>Event Name</strong>
          <span style={{ color: 'red' }}> *</span>
        </label>
        <input
          type="text"
          id="event_name"
          name="event_name"
          value={eventDetails.event_name || ''}
          onChange={handleInputChange}
          placeholder="Enter event name"
          maxLength={100}
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="event_description">
          <strong>Event Description</strong>
          <span style={{ color: 'red' }}> *</span>
        </label>
        <textarea
          id="event_description"
          name="event_description"
          value={eventDetails.event_description || ''}
          onChange={handleInputChange}
          placeholder="Enter event description"
          maxLength={400}
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="location"><strong>Location</strong><span style={{ color: 'red' }}> *</span></label>
        <input
          type="text"
          id="location"
          name="location"
          value={eventDetails.location || ''}
          onChange={handleInputChange}
          maxLength={500}
          placeholder="123 Main Street, Virtual Invite Link, or Private"
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="start_date">
          <strong>Start Date</strong>
          <span style={{ color: 'red' }}> *</span>
        </label>
        <input
          type="datetime-local"
          id="start_date"
          name="start_date"
          value={formatDateTime(eventDetails.start_date) || ''}
          onChange={handleInputChange}
          max={maxStartDateStr}
          step={60}
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="end_date">
          <strong>End Date</strong>
          <span style={{ color: 'red' }}> *</span>
        </label>
        <input
          type="datetime-local"
          id="end_date"
          name="end_date"
          value={formatDateTime(eventDetails.end_date) || ''}
          onChange={handleInputChange}
          min={minEndDate}
          max={maxEndDateStr}
          step={60}
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="event_type_id">
          <strong>Event Type</strong>
          <span style={{ color: 'red' }}> *</span>
        </label>
        <select
          id="event_type_id"
          name="event_type_id"
          value={eventDetails.event_type_id || ''}
          onChange={handleInputChange}
        >
          <option value="">Select Event Type</option>
          {availableEventTypes.map((eventType) => (
            <option key={eventType.event_type_id} value={eventType.event_type_id}>
              {eventType.event_type_name}
            </option>
          ))}
        </select>
      </div>


      <div className="form-group">
        <label htmlFor="checkin_only"><strong>Use Quick Cyne Up?</strong><span style={{ color: 'red' }}> *</span></label>
        <div>
          <label>
            <input style={{ width: '40px' }}
              type="radio"
              id="checkin_only_yes"
              name="checkin_only"
              value="true"
              checked={eventDetails.checkin_only === true}
              onChange={handleInputChange}
            />
            Yes, this event will only keep track of registrants that Cyne Up at the event.
          </label>
          <label>
            <input style={{ width: '40px' }}
              type="radio"
              id="checkin_only_no"
              name="checkin_only"
              value="false"
              checked={eventDetails.checkin_only === false}
              onChange={handleInputChange}
            />
            No, this event will have a registration and invitation process.
          </label>
        </div>
      </div>

      {!eventDetails.checkin_only && (<div className="form-group">
        <label htmlFor="has_invitation"><strong>Are you selecting Participants? (Invited Registrants To Event After They Cyne Up)</strong><span style={{ color: 'red' }}> *</span></label>
        <div>
          <label>
            <input style={{ width: '40px' }}
              type="radio"
              id="has_invitation_yes"
              name="has_invitation"
              value="true"
              checked={eventDetails.has_invitation === true}
              onChange={handleInputChange}
            />
            Yes, registrants will be selected after registering their information.
          </label>
          <label>
            <input style={{ width: '40px' }}
              type="radio"
              id="has_invitation_no"
              name="has_invitation"
              value="false"
              checked={eventDetails.has_invitation === false}
              onChange={handleInputChange}
            />
            No, all registrants that Cyne Up may attend the event.
          </label>
        </div>
      </div>)}

      {!eventDetails.checkin_only && (<div className="form-group">
        <label htmlFor="has_checkin"><strong>Are Registrants Checking In at the Event?</strong><span style={{ color: 'red' }}> *</span></label>
        <div>
          <label>
            <input style={{ width: '40px' }}
              type="radio"
              id="has_checkin_yes"
              name="has_checkin"
              value="true"
              checked={eventDetails.has_checkin === true}
              onChange={handleInputChange}
            />
            Yes, registrants check in at the event.
          </label>
          <label>
            <input style={{ width: '40px' }}
              type="radio"
              id="has_checkin_no"
              name="has_checkin"
              value="false"
              checked={eventDetails.has_checkin === false}
              onChange={handleInputChange}
            />
            No, registrants do not check in at the event.
          </label>
        </div>
      </div>)}

      {/* <div className="form-group">
        <label htmlFor="organization_registrant_limit">
          <strong>Registration Limit Per User</strong>
          <span style={{ color: 'red' }}> *</span>
        </label>
        <input
          type="number"
          id="organization_registrant_limit"
          name="organization_registrant_limit"
          value={eventDetails.organization_registrant_limit || 5}
          onChange={handleInputChange}
          placeholder="Enter a whole number between 1 and 100,000"
          required
          min={1}
          max={100000}
          step={1}
        />
      </div> */}

      <div className="form-group">
        <label htmlFor="has_judging">
          <input
            type="checkbox"
            id="has_judging"
            name="has_judging"
            checked={eventDetails.has_judging || false}
            onChange={handleInputChange}
          />
          <strong>Enable judging and rating capabilities for event</strong>
        </label>
      </div>

      {/* <div className="form-group">
        <label htmlFor="require_organization">
          <input
            type="checkbox"
            id="require_organization"
            name="require_organization"
            checked={eventDetails.require_organization || false}
            onChange={handleInputChange}
          />
          <strong>Require registrants to have an organization</strong>
        </label>
      </div> */}

      {/* <div className="form-group">
        <label>
          <strong>Billing Service</strong>
          <span style={{ color: 'red' }}> *</span>
        </label>
        <div>
          <label>
            <input style={{ width: '40px' }}
              type="radio"
              name="use_billing_service"
              value="1"
              checked={eventDetails.use_billing_service === true}
              onChange={handleInputChange}
            />
            Yes, we plan to use the site's billing service for all billing-related items.
          </label>
          <label>
            <input style={{ width: '40px' }}
              type="radio"
              name="use_billing_service"
              value="0"
              checked={eventDetails.use_billing_service === false}
              onChange={handleInputChange}
            />
            No, we plan to use our own billing system.
          </label>
        </div>
      </div> */}

      {eventDetails.use_billing_service === true ? (
        <div className="form-group">
          <label htmlFor="registrant_fee">
            <strong>Registration Cost Per Person (Does not include service charge)</strong>
            <span style={{ color: 'red' }}> *</span>
          </label>
          <input
            id="registrant_fee"
            name="registrant_fee"
            type="number"
            value={eventDetails.registrant_fee !== undefined ? eventDetails.registrant_fee : 0}
            placeholder="12.00"
            onChange={handleInputChange}
            step={0.01}

            required
          />
        </div>
      ) : (
        <div className="form-group">
          <label htmlFor="expected_registrant_count">
            <strong>Expected Number of Registrants (Will Change Cost of Event)</strong>
            <span style={{ color: 'red' }}> *</span>
          </label>
          <input
            id="expected_registrant_count"
            name="expected_registrant_count"
            type="number"
            value={eventDetails.expected_registrant_count || ''}
            placeholder="500"
            min={5}
            max={1000000}
            step={1}
            onChange={handleInputChange}

            required
          />
        </div>
      )}

      {/* <div className="form-group">
        <label>
          <strong>Invoice</strong>
          <span style={{ color: 'red' }}> *</span>
        </label>
        <div>
          <label>
            <input style={{ width: '40px' }}
              type="radio"
              name="use_invoice"
              value="1"
              checked={eventDetails.use_invoice === true}
              onChange={handleInputChange}
            />
            Send me an invoice.
          </label>
          <label>
            <input style={{ width: '40px' }}
              type="radio"
              name="use_invoice"
              value="0"
              checked={eventDetails.use_invoice === false}
              onChange={handleInputChange}
            />
            Pay online when the event is finished.
          </label>
        </div>
      </div> */}
      <br />

      <div className='buttonsRow'>
        <div className='buttonsLeft'>
          <button type="button" className='cancelButton' onClick={handleCancel}>
            Cancel
          </button>

        </div>
        <button type="button" onClick={handleUpdate} disabled={currentEvent?.is_locked}>
          Update
        </button>
      </div>
    </form>
  );
};
